import { createFileRoute } from '@tanstack/react-router';
import { Check, Mail, Phone } from 'lucide-react';
import {
    Fragment,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { z } from 'zod';

import { PhoneAppBarTourReplayButton } from '@apps/web/components/layout/phone_app_bar_slots';
import {
    TrainingModePageTourReplayButton,
    TrainingModePageTourScope,
} from '@apps/web/components/page_tour/training_mode_page_tour';
import { VOICE_CALLS_PAGE_TOUR_STEPS } from '@apps/web/components/page_tour/training_mode_tour_configs';
import { ConversationsPageTabs } from '@apps/web/components/shared/messages/conversations_page_tabs';
import {
    INBOX_PILL_COMPACT_CLASS,
    InboxFilterPill,
} from '@apps/web/components/shared/messages/inbox_filter_pills';
import { OrganizationConversationDetailPane } from '@apps/web/components/shared/messages/organization_conversation_detail_pane';
import { Combobox } from '@apps/web/components/ui/combobox';
import {
    CommandEmpty,
    CommandGroup,
    CommandItem,
} from '@apps/web/components/ui/command';
import { EmptyState } from '@apps/web/components/ui/empty_state';
import { FilterBar } from '@apps/web/components/ui/filter_bar';
import { SearchInput } from '@apps/web/components/ui/search_input';
import { Spinner } from '@apps/web/components/ui/spinner';
import { CallDetailDialog } from '@apps/web/components/voice_calls/call_detail_dialog';
import { CallListRows } from '@apps/web/components/voice_calls/call_list_rows';
import { useCallThreadConversation } from '@apps/web/components/voice_calls/use_call_thread_conversation';
import {
    SearchOlderVoiceCallsButton,
    VoiceCallsEmptyState,
} from '@apps/web/components/voice_calls/voice_calls_empty_state';
import { useOrganizationTimezone } from '@apps/web/context/timezone_provider';
import { useCoalescedCallback } from '@apps/web/hooks/use_coalesced_callback';
import { useDebounce } from '@apps/web/hooks/use_debounce';
import { useDeepLinkOrganization } from '@apps/web/hooks/use_deep_link_organization';
import { useListenForOrganizationEvent } from '@apps/web/hooks/use_listen_for_organization_event';
import {
    localFilterMemoryStorageKey,
    useLocalFilterMemory,
} from '@apps/web/hooks/use_local_filter_memory';
import { useIsMobile } from '@apps/web/hooks/use_mobile';
import { useOnModeChange } from '@apps/web/hooks/use_on_mode_change';
import { useOrganizationId } from '@apps/web/hooks/use_organization_id';
import { useFormatPersonName } from '@apps/web/hooks/use_person_name_format';
import { useResourceOrganizationLookup } from '@apps/web/hooks/use_resource_organization_lookup';
import { useThrottledCallback } from '@apps/web/hooks/use_throttled_callback';
import { ACTIVITY_TOPIC_FILTER_OPTIONS } from '@apps/web/lib/activity_topic_filter_options';
import { formatPhoneNumber } from '@apps/web/lib/contact_utils';
import { useMode } from '@apps/web/lib/mode_store';
import { cn } from '@apps/web/lib/utils';
import {
    buildVoiceCallFilterDimensions,
    voiceCallFilterChips,
    voiceCallFilterChipValues,
    type VoiceCallFilterKey,
} from '@apps/web/routes/_app/(main)/voice-calls-filter-chips';
import { type ActivityTopicFilter } from '@apps/web/swr_data_fetching/activity_event_queries';
import { useUser } from '@apps/web/swr_data_fetching/current_user_queries';
import {
    useConversationActions,
    useInfiniteVoiceCalls,
    useOrganizationCallRoutingDestinations,
    useOrganizationVoiceCall,
    useVoiceCallFilterParticipants,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import { useOrganizationOffices } from '@apps/web/swr_data_fetching/organization_queries';
import { ApiError } from '@libraries/typescript/api_client';
import { type OrganizationOffice } from '@libraries/typescript/api_client/schema_types';

export const CALLS_PAGE_SIZE = 15;

export function isVoiceCallsPageLoading(
    filterMemoryHydrated: boolean,
    callsLoading: boolean,
): boolean {
    return !filterMemoryHydrated || callsLoading;
}
// Distance from the bottom of the call list at which the next page loads.
const INFINITE_SCROLL_THRESHOLD_PX = 200;
const ALL_CAREGIVERS_VALUE = '__all__';
const ALL_CLIENTS_VALUE = '__all__';
const ALL_DIRECTIONS_VALUE = 'all';
const ALL_CALL_TYPES_VALUE = 'all';
const ALL_TRANSFER_STATUSES_VALUE = 'all';
const ALL_TRANSFER_DESTINATIONS_VALUE = 'all';
const ALL_LINES_VALUE = 'all';
const ALL_NOTES_VALUE = 'all';
const ALL_VOICEMAIL_STATUSES_VALUE = 'all';
const ALL_OFFICES_VALUE = 'all';
const ALL_TOPICS_VALUE = 'all';
const NEEDS_REVIEW_NOTES_VALUE = 'needs_review';
const SEARCH_DEBOUNCE_DELAY_MS = 300;
const VOICE_CALL_REALTIME_REFRESH_DELAY_MS = 3_000;

type VoiceCallRealtimeRefreshOptions = {
    readonly organizationId: string | null;
    readonly selectedCallId: string | null;
    readonly refreshCallsPage: () => Promise<unknown> | unknown;
    readonly refreshSelectedCallDetail: () => Promise<unknown> | unknown;
};

/**
 * Keeps voice-call list and detail data current without stacking requests.
 *
 * The event handler remains unthrottled so an event for the selected call is
 * never replaced by a later unrelated event. Each refresh is throttled and
 * single-flight independently after the resource decision is made.
 */
export function useVoiceCallRealtimeRefresh({
    organizationId,
    selectedCallId,
    refreshCallsPage,
    refreshSelectedCallDetail,
}: VoiceCallRealtimeRefreshOptions) {
    const coalescedCallsPageRefresh = useCoalescedCallback(refreshCallsPage);
    const throttledCallsPageRefresh = useThrottledCallback(
        coalescedCallsPageRefresh,
        VOICE_CALL_REALTIME_REFRESH_DELAY_MS,
    );
    const coalescedSelectedCallRefresh = useCoalescedCallback(
        refreshSelectedCallDetail,
    );
    const throttledSelectedCallRefresh = useThrottledCallback(
        coalescedSelectedCallRefresh,
        VOICE_CALL_REALTIME_REFRESH_DELAY_MS,
    );

    useListenForOrganizationEvent(
        'voice_call_updated',
        (event) => {
            if (event.resource.type !== 'voice_call') {
                return;
            }

            throttledCallsPageRefresh();
            if (event.resource.id === selectedCallId) {
                throttledSelectedCallRefresh();
            }
        },
        { organizationId },
    );
}

type VoiceCallDirection = 'inbound' | 'outbound';
type CallDirectionFilterValue = 'all' | VoiceCallDirection;
type CallTypeFilterValue = 'all' | 'client' | 'caregiver' | 'notification';
type TransferStatusFilterValue = 'all' | 'transferred' | 'not_transferred';
// The shared activity topic taxonomy minus "messages", which only maps to
// SMS escalation events and can never match a call. A call's topic is
// derived from its voice-call-note activity events server-side.
type CallTopicValue = Exclude<ActivityTopicFilter, 'messages'>;
type CallTopicFilterValue = typeof ALL_TOPICS_VALUE | CallTopicValue;
type NotesFilterValue =
    | typeof ALL_NOTES_VALUE
    | typeof NEEDS_REVIEW_NOTES_VALUE;
type VoicemailStatusFilterValue =
    | typeof ALL_VOICEMAIL_STATUSES_VALUE
    | 'voicemail'
    | 'not_voicemail';

function isCallTopicOption(option: {
    readonly value: ActivityTopicFilter;
    readonly label: string;
}): option is { readonly value: CallTopicValue; readonly label: string } {
    return option.value !== 'messages';
}

export const CALL_TOPIC_OPTIONS =
    ACTIVITY_TOPIC_FILTER_OPTIONS.filter(isCallTopicOption);

function isCallTopicValue(value: string): value is CallTopicValue {
    return CALL_TOPIC_OPTIONS.some((option) => option.value === value);
}

export type PendingVoiceCallFilters = {
    readonly caregiverId: string;
    readonly clientId: string;
    readonly dateFrom: string | undefined;
    readonly dateTo: string | undefined;
    readonly direction: CallDirectionFilterValue;
    readonly callType: CallTypeFilterValue;
    readonly transferStatus: TransferStatusFilterValue;
    readonly transferDestinationId: string;
    readonly lineNumber: string;
    readonly voicemailStatus: VoicemailStatusFilterValue;
    readonly messageLeft: boolean;
    readonly notesFilter: NotesFilterValue;
    readonly topic: CallTopicFilterValue;
    readonly search: string;
    readonly officeIds: ReadonlyArray<string>;
};

export type AppliedFilters = {
    readonly caregiverId: string | undefined;
    readonly clientId: string | undefined;
    readonly dateFrom: string | undefined;
    readonly dateTo: string | undefined;
    readonly direction: VoiceCallDirection | undefined;
    readonly callType: 'client' | 'caregiver' | 'notification' | undefined;
    readonly transferredToBackOffice: boolean | undefined;
    readonly transferDestinationId: string | undefined;
    readonly lineNumber: string | undefined;
    readonly voicemail: boolean | undefined;
    readonly messageLeft: true | undefined;
    readonly notesNeedsReview: boolean | undefined;
    readonly topic: CallTopicValue | undefined;
    readonly search: string | undefined;
    readonly officeIds: ReadonlyArray<string> | undefined;
};

export const voiceCallFilterMemoryPayloadSchema = z.object({
    caregiverId: z.string().nullable(),
    clientId: z.string().nullable(),
    dateFrom: z.string().nullable(),
    dateTo: z.string().nullable(),
    direction: z.enum(['inbound', 'outbound']).nullable(),
    callType: z.enum(['client', 'caregiver', 'notification']).nullable(),
    transferredToBackOffice: z.boolean().nullable(),
    transferDestinationId: z.string().nullable(),
    // Defaulted so views saved before the line filter existed still parse.
    lineNumber: z.string().nullable().default(null),
    voicemail: z.boolean().nullable(),
    messageLeft: z.literal(true).nullable().default(null),
    notesNeedsReview: z.boolean().nullable(),
    topic: z.string().refine(isCallTopicValue).nullable(),
    officeIds: z.array(z.string()),
});

export type VoiceCallFilterMemoryPayload = z.infer<
    typeof voiceCallFilterMemoryPayloadSchema
>;

const voiceCallFilterMemorySchema = z.object({
    version: z.literal(1),
    filters: voiceCallFilterMemoryPayloadSchema,
});

export type VoiceCallFilterMemory = z.infer<typeof voiceCallFilterMemorySchema>;

const EMPTY_FILTERS: AppliedFilters = {
    caregiverId: undefined,
    clientId: undefined,
    dateFrom: undefined,
    dateTo: undefined,
    direction: undefined,
    callType: undefined,
    transferredToBackOffice: undefined,
    transferDestinationId: undefined,
    lineNumber: undefined,
    voicemail: undefined,
    messageLeft: undefined,
    notesNeedsReview: undefined,
    topic: undefined,
    search: undefined,
    officeIds: undefined,
};

function sortOfficeIds(officeIds: ReadonlyArray<string>): Array<string> {
    return [...officeIds].sort((a, b) => a.localeCompare(b));
}

function getOfficeIdsForApi(
    officeIds: ReadonlyArray<string>,
): Array<string> | undefined {
    const sortedOfficeIds = sortOfficeIds(officeIds);
    return sortedOfficeIds.length > 0 ? sortedOfficeIds : undefined;
}

export function getOfficeFilterDisplayValue(
    selectedOfficeIds: ReadonlyArray<string>,
    officeNameById: ReadonlyMap<string, string>,
): string | undefined {
    if (selectedOfficeIds.length === 0) {
        return undefined;
    }
    if (selectedOfficeIds.length === 1) {
        const [selectedOfficeId] = selectedOfficeIds;
        if (selectedOfficeId === undefined) {
            return undefined;
        }
        return officeNameById.get(selectedOfficeId) ?? '1 office';
    }
    return `${selectedOfficeIds.length} offices`;
}

export function getSearchValueForVoiceCallFilters({
    pendingSearch,
    debouncedSearch,
}: {
    readonly pendingSearch: string;
    readonly debouncedSearch: string;
}): string {
    return pendingSearch.trim() === '' ? '' : debouncedSearch;
}

export function buildAppliedVoiceCallFilters(
    pendingFilters: PendingVoiceCallFilters,
): AppliedFilters {
    return {
        caregiverId:
            pendingFilters.caregiverId === ALL_CAREGIVERS_VALUE
                ? undefined
                : pendingFilters.caregiverId,
        clientId:
            pendingFilters.clientId === ALL_CLIENTS_VALUE
                ? undefined
                : pendingFilters.clientId,
        dateFrom: pendingFilters.dateFrom,
        dateTo: pendingFilters.dateTo,
        direction:
            pendingFilters.direction === ALL_DIRECTIONS_VALUE
                ? undefined
                : pendingFilters.direction,
        callType:
            pendingFilters.callType === ALL_CALL_TYPES_VALUE
                ? undefined
                : pendingFilters.callType,
        transferredToBackOffice:
            pendingFilters.transferStatus === 'transferred'
                ? true
                : pendingFilters.transferStatus === 'not_transferred'
                  ? false
                  : undefined,
        transferDestinationId:
            pendingFilters.transferDestinationId ===
            ALL_TRANSFER_DESTINATIONS_VALUE
                ? undefined
                : pendingFilters.transferDestinationId,
        lineNumber:
            pendingFilters.lineNumber === ALL_LINES_VALUE
                ? undefined
                : pendingFilters.lineNumber,
        voicemail:
            pendingFilters.voicemailStatus === 'voicemail'
                ? true
                : pendingFilters.voicemailStatus === 'not_voicemail'
                  ? false
                  : undefined,
        messageLeft: pendingFilters.messageLeft ? true : undefined,
        notesNeedsReview:
            pendingFilters.notesFilter === NEEDS_REVIEW_NOTES_VALUE
                ? true
                : undefined,
        topic:
            pendingFilters.topic === ALL_TOPICS_VALUE
                ? undefined
                : pendingFilters.topic,
        search: pendingFilters.search.trim() || undefined,
        officeIds: getOfficeIdsForApi(pendingFilters.officeIds),
    };
}

function getAppliedFiltersKey(filters: AppliedFilters): string {
    return JSON.stringify({
        caregiverId: filters.caregiverId ?? null,
        clientId: filters.clientId ?? null,
        dateFrom: filters.dateFrom ?? null,
        dateTo: filters.dateTo ?? null,
        direction: filters.direction ?? null,
        callType: filters.callType ?? null,
        transferredToBackOffice: filters.transferredToBackOffice ?? null,
        transferDestinationId: filters.transferDestinationId ?? null,
        lineNumber: filters.lineNumber ?? null,
        voicemail: filters.voicemail ?? null,
        messageLeft: filters.messageLeft ?? null,
        notesNeedsReview: filters.notesNeedsReview ?? null,
        topic: filters.topic ?? null,
        search: filters.search ?? null,
        officeIds: sortOfficeIds(filters.officeIds ?? []),
    });
}

// Search text and the selected call are temporary navigation state. Filter
// memory restores only repeatable filters.
export function captureVoiceCallFilterMemoryPayload(
    filters: AppliedFilters,
): VoiceCallFilterMemoryPayload {
    return {
        caregiverId: filters.caregiverId ?? null,
        clientId: filters.clientId ?? null,
        dateFrom: filters.dateFrom ?? null,
        dateTo: filters.dateTo ?? null,
        direction: filters.direction ?? null,
        callType: filters.callType ?? null,
        transferredToBackOffice: filters.transferredToBackOffice ?? null,
        transferDestinationId: filters.transferDestinationId ?? null,
        lineNumber: filters.lineNumber ?? null,
        voicemail: filters.voicemail ?? null,
        messageLeft: filters.messageLeft ?? null,
        notesNeedsReview: filters.notesNeedsReview ?? null,
        topic: filters.topic ?? null,
        officeIds: sortOfficeIds(filters.officeIds ?? []),
    };
}

export function appliedFiltersFromVoiceCallFilterMemory(
    payload: VoiceCallFilterMemoryPayload,
): AppliedFilters {
    return {
        caregiverId: payload.caregiverId ?? undefined,
        clientId: payload.clientId ?? undefined,
        dateFrom: payload.dateFrom ?? undefined,
        dateTo: payload.dateTo ?? undefined,
        direction: payload.direction ?? undefined,
        callType: payload.callType ?? undefined,
        transferredToBackOffice: payload.transferredToBackOffice ?? undefined,
        transferDestinationId: payload.transferDestinationId ?? undefined,
        lineNumber: payload.lineNumber ?? undefined,
        voicemail: payload.voicemail ?? undefined,
        messageLeft: payload.messageLeft ?? undefined,
        notesNeedsReview: payload.notesNeedsReview ?? undefined,
        topic: payload.topic ?? undefined,
        search: undefined,
        officeIds: payload.officeIds.length > 0 ? payload.officeIds : undefined,
    };
}

function formatClientName(client: {
    readonly first_name?: string | null;
    readonly last_name?: string | null;
    readonly preferred_name?: string | null;
}): string {
    const first = client.preferred_name ?? client.first_name ?? '';
    const last = client.last_name ?? '';
    const combined = `${first} ${last}`.trim();
    return combined || 'Unnamed client';
}

export function OfficeFilterCombobox({
    offices,
    selectedOfficeIds,
    onSelectedOfficeIdsChange,
}: {
    readonly offices: ReadonlyArray<OrganizationOffice>;
    readonly selectedOfficeIds: ReadonlyArray<string>;
    readonly onSelectedOfficeIdsChange: (
        officeIds: ReadonlyArray<string>,
    ) => void;
}) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const officeNameById = useMemo(
        () =>
            new Map(offices.map((office) => [office.id, office.display_name])),
        [offices],
    );
    const displayValue = getOfficeFilterDisplayValue(
        selectedOfficeIds,
        officeNameById,
    );

    function toggleOffice(officeId: string) {
        onSelectedOfficeIdsChange(
            selectedOfficeIds.includes(officeId)
                ? selectedOfficeIds.filter((id) => id !== officeId)
                : [...selectedOfficeIds, officeId],
        );
    }

    return (
        <Combobox
            open={open}
            onOpenChange={setOpen}
            displayValue={displayValue}
            placeholder="All offices"
            searchValue={search}
            onSearchValueChange={setSearch}
            searchPlaceholder="Search offices…"
            contentClassName="w-[260px]"
            matchTriggerWidth={false}
            fullWidth={false}
            triggerClassName="h-9 w-[150px] text-sm"
            triggerProps={{ 'aria-label': 'Office' }}
        >
            <CommandEmpty>No offices found.</CommandEmpty>
            <CommandGroup>
                <CommandItem
                    value={ALL_OFFICES_VALUE}
                    onSelect={() => {
                        onSelectedOfficeIdsChange([]);
                        setOpen(false);
                    }}
                >
                    <Check
                        className={cn(
                            'mr-2 h-4 w-4',
                            selectedOfficeIds.length === 0
                                ? 'opacity-100'
                                : 'opacity-0',
                        )}
                    />
                    All offices
                </CommandItem>
                {offices.map((office) => {
                    const isSelected = selectedOfficeIds.includes(office.id);
                    return (
                        <CommandItem
                            key={office.id}
                            value={office.display_name}
                            onSelect={() => toggleOffice(office.id)}
                        >
                            <div
                                className={cn(
                                    'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border',
                                    isSelected
                                        ? 'border-foreground bg-foreground'
                                        : 'border-muted-foreground bg-background',
                                )}
                            >
                                <Check
                                    className={cn(
                                        'h-4 w-4',
                                        isSelected
                                            ? 'text-primary-foreground'
                                            : 'invisible',
                                    )}
                                />
                            </div>
                            <span className="truncate">
                                {office.display_name}
                            </span>
                        </CommandItem>
                    );
                })}
            </CommandGroup>
        </Combobox>
    );
}

export const voiceCallsSearchSchema = z.object({
    direction: z.enum(['inbound', 'outbound']).optional(),
    voiceCallId: z.string().optional(),
    organizationId: z.string().optional(),
    // A malformed link should leave the tab's mode unchanged, not crash it.
    mode: z.enum(['live', 'sandbox']).optional().catch(undefined),
});

export const Route = createFileRoute('/_app/(main)/voice-calls')({
    component: VoiceCallsRoute,
    validateSearch: voiceCallsSearchSchema,
    head: () => ({
        meta: [
            {
                title: 'Voice Calls | Phoebe',
            },
        ],
    }),
});

function VoiceCallsRoute() {
    const { formatName } = useFormatPersonName();
    const navigate = Route.useNavigate();
    const {
        direction: directionFromSearch,
        voiceCallId: voiceCallIdFromSearch,
        organizationId: deepLinkOrganizationId,
        mode: deepLinkMode,
    } = Route.useSearch();
    const { isWaitingForDeepLinkOrganization } = useDeepLinkOrganization(
        deepLinkOrganizationId,
        deepLinkMode,
    );
    const organizationId = useOrganizationId();
    const mode = useMode() ?? 'live';
    const { timezone } = useOrganizationTimezone();
    const { data: user } = useUser({
        reason: 'calls-page',
    });
    const silentListeningLabelSettingsEnabled = false;
    const isMobile = useIsMobile();

    // Pending filter state (not yet applied)
    const [pendingCaregiverId, setPendingCaregiverId] =
        useState(ALL_CAREGIVERS_VALUE);
    const [pendingClientId, setPendingClientId] = useState(ALL_CLIENTS_VALUE);
    const [pendingDateFrom, setPendingDateFrom] = useState<string | undefined>(
        undefined,
    );
    const [pendingDateTo, setPendingDateTo] = useState<string | undefined>(
        undefined,
    );
    const [pendingDirection, setPendingDirection] =
        useState<CallDirectionFilterValue>(
            directionFromSearch ?? ALL_DIRECTIONS_VALUE,
        );
    const [pendingCallType, setPendingCallType] =
        useState<CallTypeFilterValue>(ALL_CALL_TYPES_VALUE);
    const [pendingTransferStatus, setPendingTransferStatus] =
        useState<TransferStatusFilterValue>(ALL_TRANSFER_STATUSES_VALUE);
    const [pendingTransferDestinationId, setPendingTransferDestinationId] =
        useState(ALL_TRANSFER_DESTINATIONS_VALUE);
    const [pendingLineNumber, setPendingLineNumber] = useState(ALL_LINES_VALUE);
    const [pendingVoicemailStatus, setPendingVoicemailStatus] =
        useState<VoicemailStatusFilterValue>(ALL_VOICEMAIL_STATUSES_VALUE);
    const [pendingMessageLeft, setPendingMessageLeft] = useState(false);
    const [pendingNotesFilter, setPendingNotesFilter] =
        useState<NotesFilterValue>(ALL_NOTES_VALUE);
    const [pendingTopic, setPendingTopic] =
        useState<CallTopicFilterValue>(ALL_TOPICS_VALUE);
    const [pendingSearch, setPendingSearch] = useState('');
    const debouncedSearch = useDebounce(
        pendingSearch.trim(),
        SEARCH_DEBOUNCE_DELAY_MS,
    );
    const [pendingOfficeIds, setPendingOfficeIds] = useState<
        ReadonlyArray<string>
    >([]);

    // Applied filter state (sent to backend)
    const [appliedFilters, setAppliedFilters] = useState<AppliedFilters>({
        ...EMPTY_FILTERS,
        direction: directionFromSearch,
    });
    const appliedDirectionLinkRef = useRef<string | null>(null);
    const filterMemoryStorageKey =
        user?.id && organizationId
            ? localFilterMemoryStorageKey(
                  'voice-calls',
                  organizationId,
                  user.id,
                  mode,
              )
            : null;
    const currentFilterMemoryPayload = useMemo(
        () => captureVoiceCallFilterMemoryPayload(appliedFilters),
        [appliedFilters],
    );
    const setPendingFiltersFromApplied = useCallback(
        (filters: AppliedFilters) => {
            setPendingCaregiverId(filters.caregiverId ?? ALL_CAREGIVERS_VALUE);
            setPendingClientId(filters.clientId ?? ALL_CLIENTS_VALUE);
            setPendingDateFrom(filters.dateFrom);
            setPendingDateTo(filters.dateTo);
            setPendingDirection(filters.direction ?? ALL_DIRECTIONS_VALUE);
            setPendingCallType(filters.callType ?? ALL_CALL_TYPES_VALUE);
            setPendingTransferStatus(
                filters.transferredToBackOffice === true
                    ? 'transferred'
                    : filters.transferredToBackOffice === false
                      ? 'not_transferred'
                      : ALL_TRANSFER_STATUSES_VALUE,
            );
            setPendingTransferDestinationId(
                filters.transferDestinationId ??
                    ALL_TRANSFER_DESTINATIONS_VALUE,
            );
            setPendingLineNumber(filters.lineNumber ?? ALL_LINES_VALUE);
            setPendingVoicemailStatus(
                filters.voicemail === true
                    ? 'voicemail'
                    : filters.voicemail === false
                      ? 'not_voicemail'
                      : ALL_VOICEMAIL_STATUSES_VALUE,
            );
            setPendingMessageLeft(filters.messageLeft === true);
            setPendingNotesFilter(
                filters.notesNeedsReview
                    ? NEEDS_REVIEW_NOTES_VALUE
                    : ALL_NOTES_VALUE,
            );
            setPendingTopic(filters.topic ?? ALL_TOPICS_VALUE);
            setPendingSearch('');
            setPendingOfficeIds(filters.officeIds ?? []);
        },
        [],
    );

    const filterMemoryValue = useMemo<VoiceCallFilterMemory>(
        () => ({ version: 1, filters: currentFilterMemoryPayload }),
        [currentFilterMemoryPayload],
    );
    const { isHydrated: isFilterMemoryHydrated } = useLocalFilterMemory({
        storageKey: filterMemoryStorageKey,
        schema: voiceCallFilterMemorySchema,
        value: filterMemoryValue,
        defaultValue: () => ({
            version: 1,
            filters: captureVoiceCallFilterMemoryPayload({
                ...EMPTY_FILTERS,
                direction: directionFromSearch,
            }),
        }),
        onRestore: (memory) => {
            const rememberedFilters = appliedFiltersFromVoiceCallFilterMemory(
                memory.filters,
            );
            const nextFilters = {
                ...rememberedFilters,
                // An explicit inbound/outbound link wins for this visit.
                direction: directionFromSearch ?? rememberedFilters.direction,
            };
            setPendingFiltersFromApplied(nextFilters);
            setAppliedFilters(nextFilters);
        },
    });
    useEffect(() => {
        if (!isFilterMemoryHydrated) {
            return;
        }
        if (directionFromSearch === undefined) {
            appliedDirectionLinkRef.current = null;
            return;
        }
        const directionLinkKey = `${filterMemoryStorageKey}:${directionFromSearch}`;
        if (appliedDirectionLinkRef.current === directionLinkKey) {
            return;
        }
        appliedDirectionLinkRef.current = directionLinkKey;
        if (appliedFilters.direction === directionFromSearch) {
            return;
        }
        const nextFilters = {
            ...appliedFilters,
            direction: directionFromSearch,
        };
        setPendingFiltersFromApplied(nextFilters);
        setAppliedFilters(nextFilters);
    }, [
        appliedFilters,
        directionFromSearch,
        filterMemoryStorageKey,
        isFilterMemoryHydrated,
        setPendingFiltersFromApplied,
    ]);

    const [selectedCallId, setSelectedCallId] = useState<string | null>(
        voiceCallIdFromSearch ?? null,
    );
    const [mobileDialogCallId, setMobileDialogCallId] = useState<string | null>(
        null,
    );
    const pendingFiltersForApi = useMemo(
        () =>
            buildAppliedVoiceCallFilters({
                caregiverId: pendingCaregiverId,
                clientId: pendingClientId,
                dateFrom: pendingDateFrom,
                dateTo: pendingDateTo,
                direction: pendingDirection,
                callType: pendingCallType,
                transferStatus: pendingTransferStatus,
                transferDestinationId: pendingTransferDestinationId,
                lineNumber: pendingLineNumber,
                voicemailStatus: pendingVoicemailStatus,
                messageLeft: pendingMessageLeft,
                notesFilter: pendingNotesFilter,
                topic: pendingTopic,
                search: getSearchValueForVoiceCallFilters({
                    pendingSearch,
                    debouncedSearch,
                }),
                officeIds: pendingOfficeIds,
            }),
        [
            debouncedSearch,
            pendingCallType,
            pendingCaregiverId,
            pendingClientId,
            pendingDateFrom,
            pendingDateTo,
            pendingDirection,
            pendingLineNumber,
            pendingMessageLeft,
            pendingNotesFilter,
            pendingOfficeIds,
            pendingSearch,
            pendingTopic,
            pendingTransferDestinationId,
            pendingTransferStatus,
            pendingVoicemailStatus,
        ],
    );
    const hasFilterChanges =
        getAppliedFiltersKey(pendingFiltersForApi) !==
        getAppliedFiltersKey(appliedFilters);
    const hasActiveFilters =
        appliedFilters.caregiverId !== undefined ||
        appliedFilters.clientId !== undefined ||
        appliedFilters.dateFrom !== undefined ||
        appliedFilters.dateTo !== undefined ||
        appliedFilters.direction !== undefined ||
        appliedFilters.callType !== undefined ||
        appliedFilters.transferredToBackOffice !== undefined ||
        appliedFilters.transferDestinationId !== undefined ||
        appliedFilters.lineNumber !== undefined ||
        appliedFilters.voicemail !== undefined ||
        appliedFilters.messageLeft !== undefined ||
        appliedFilters.notesNeedsReview !== undefined ||
        appliedFilters.topic !== undefined ||
        appliedFilters.search !== undefined ||
        appliedFilters.officeIds !== undefined;

    const voiceCallFilters = useMemo(
        () => ({
            direction: appliedFilters.direction,
            caregiver_id: appliedFilters.caregiverId,
            client_id: appliedFilters.clientId,
            date_from: appliedFilters.dateFrom,
            date_to: appliedFilters.dateTo,
            call_type: appliedFilters.callType,
            transferred_to_back_office: appliedFilters.transferredToBackOffice,
            transfer_destination_id: appliedFilters.transferDestinationId,
            line_number: appliedFilters.lineNumber,
            voicemail: appliedFilters.voicemail,
            message_left: appliedFilters.messageLeft,
            notes_needs_review: appliedFilters.notesNeedsReview,
            topic: appliedFilters.topic,
            search: appliedFilters.search,
            office_id: appliedFilters.officeIds
                ? [...appliedFilters.officeIds]
                : undefined,
        }),
        [appliedFilters],
    );
    const voiceCallFiltersReady = isFilterMemoryHydrated;

    const {
        calls: pageCalls,
        error: callsError,
        isLoading: isLoadingCalls,
        isLoadingMore: isLoadingMoreCalls,
        isValidating: isValidatingCalls,
        hasMore: hasMoreCalls,
        canSearchOlderCalls,
        loadMore: loadMoreCalls,
        refreshFirstPage: refreshFirstCallsPage,
    } = useInfiniteVoiceCalls(
        voiceCallFiltersReady ? organizationId : null,
        { reason: 'calls-page' },
        CALLS_PAGE_SIZE,
        voiceCallFilters,
    );
    const isPageLoading = isVoiceCallsPageLoading(
        voiceCallFiltersReady,
        isLoadingCalls,
    );
    const { data: offices } = useOrganizationOffices(organizationId, {
        reason: 'voice-calls-offices',
    });
    const officeOptions = useMemo(
        () =>
            [...(offices ?? [])].sort((a, b) =>
                a.display_name.localeCompare(b.display_name),
            ),
        [offices],
    );
    const { data: callRoutingDestinations } =
        useOrganizationCallRoutingDestinations(organizationId);
    const transferDestinationOptions = useMemo(
        () =>
            (callRoutingDestinations ?? []).filter(
                (destination) => destination.is_active,
            ),
        [callRoutingDestinations],
    );

    // Resolve caregiver/client display names for the active-filter chips. The
    // popover comboboxes fetch the same participant list, so this SWR call is
    // deduped and does not add a meaningful extra request.
    const { data: filterParticipants } = useVoiceCallFilterParticipants(
        organizationId,
        { reason: 'voice-calls-active-filter-chips' },
    );
    const lineOptions = useMemo(
        () => filterParticipants?.lines ?? [],
        [filterParticipants?.lines],
    );

    // Write the selected call into the `voiceCallId` URL search param so each
    // call has a shareable, bookmarkable URL and browser back/forward works.
    // Other search params are preserved. Auto-selection and filter resets use
    // `replace` so they do not create extra history entries.
    const selectVoiceCall = useCallback(
        (callId: string | null, options?: { readonly replace?: boolean }) => {
            void navigate({
                to: '/voice-calls',
                search: (previous) => ({
                    ...previous,
                    voiceCallId: callId ?? undefined,
                }),
                replace: options?.replace ?? false,
            });
        },
        [navigate],
    );

    // Keep the selected call in sync with the `voiceCallId` URL param so
    // deep links and browser back/forward navigation update the UI.
    useEffect(() => {
        // Mobile opens details in a dialog; the desktop list uses the inline
        // right-hand detail pane instead.
        if (isMobile) {
            setMobileDialogCallId(voiceCallIdFromSearch ?? null);
            return;
        }
        setSelectedCallId(voiceCallIdFromSearch ?? null);
    }, [isMobile, voiceCallIdFromSearch]);

    useOnModeChange(() => {
        // Filter memory is mode-scoped and restores the destination mode.
        selectVoiceCall(null, { replace: true });
    });

    // Auto-select the first call on desktop when no call is deep-linked.
    useEffect(() => {
        if (voiceCallIdFromSearch) {
            return;
        }
        if (isMobile || isValidatingCalls) {
            return;
        }

        if (pageCalls.length === 0) {
            return;
        }

        const [firstCall] = pageCalls;
        if (!firstCall) {
            return;
        }

        selectVoiceCall(firstCall.id, { replace: true });
    }, [
        isMobile,
        isValidatingCalls,
        pageCalls,
        selectVoiceCall,
        voiceCallIdFromSearch,
    ]);

    // PHO-10343: when a deep-linked call already lives in the loaded pages,
    // scroll the sidebar to its row once. The detail pane loads independently
    // via useOrganizationVoiceCall, so we don't auto-paginate to find calls
    // older than the loaded window — doing so used to hammer the API and,
    // when SWR returned cached pages synchronously, exceed React's max
    // update depth (React #185).
    const scrolledToCallIdRef = useRef<string | null>(null);
    useEffect(() => {
        if (!selectedCallId || isMobile) {
            return;
        }
        if (scrolledToCallIdRef.current === selectedCallId) {
            return;
        }
        const row = document.getElementById(`voice-call-row-${selectedCallId}`);
        if (!row) {
            return;
        }
        row.scrollIntoView({ block: 'nearest' });
        scrolledToCallIdRef.current = selectedCallId;
    }, [selectedCallId, isMobile, pageCalls]);

    const selectedCallListItem = useMemo(() => {
        if (!selectedCallId || isMobile) {
            return null;
        }

        return pageCalls.find((call) => call.id === selectedCallId) ?? null;
    }, [isMobile, pageCalls, selectedCallId]);
    // A call opens as its person's thread in this pane with the call card
    // showing. The caregiver id lets a caregiver's newest thread stand in
    // when no thread has the call's number.
    const threadCaregiverId =
        selectedCallListItem && selectedCallListItem.kind !== 'notification'
            ? (selectedCallListItem.caregiver_id ?? null)
            : null;
    // Closing the card keeps the row selected (deselecting would just let
    // the page auto-select the first row again), so dismissal is tracked on
    // its own and clicking the row clears it to reopen the card.
    const [dismissedCardCallId, setDismissedCardCallId] = useState<
        string | null
    >(null);
    const shouldFetchSelectedCallDetail =
        (!isMobile && selectedCallId !== null) || !!voiceCallIdFromSearch;
    const voiceCallIdForDetail = shouldFetchSelectedCallDetail
        ? (selectedCallId ?? voiceCallIdFromSearch ?? null)
        : null;
    const {
        data: selectedCallDetail,
        error: selectedCallDetailError,
        isLoading: isLoadingSelectedCallDetail,
        mutate: mutateSelectedCallDetail,
    } = useOrganizationVoiceCall(organizationId, voiceCallIdForDetail, {
        reason: 'calls-page-detail',
    });
    const isMissingVoiceCallInCurrentOrganization =
        !!voiceCallIdFromSearch &&
        selectedCallDetailError instanceof ApiError &&
        selectedCallDetailError.status === 404;
    const { isResolvingAcrossOrganizations } = useResourceOrganizationLookup({
        enabled: isMissingVoiceCallInCurrentOrganization,
        resourceId: voiceCallIdFromSearch,
        resourceType: 'voice_call',
    });
    // List payloads omit full call fields; the detail endpoint refetch keeps
    // recordings, SOT transcripts, and actions current without bloating lists.
    const selectedCall = selectedCallDetail ?? selectedCallListItem;
    // Notification calls have no person to hold a thread with and keep the
    // call detail.
    const threadCall =
        !isMobile && selectedCall && selectedCall.kind !== 'notification'
            ? selectedCall
            : null;
    const threadCallPhone = threadCall
        ? (threadCall.caller_phone ??
          (threadCall.direction === 'inbound'
              ? threadCall.from_number
              : threadCall.to_number))
        : null;
    const {
        conversationId: threadConversationId,
        isLoading: isLoadingThreadConversation,
        pageMutators: threadPageMutators,
    } = useCallThreadConversation({
        organizationId,
        caregiverId: threadCaregiverId,
        callPhone: threadCallPhone,
    });
    // Read-state changes refresh this thread's row; the Calls tab has no
    // unread badge of its own to refresh.
    const { markAsRead, markAsUnread } = useConversationActions(
        organizationId,
        threadPageMutators,
        () => Promise.resolve(),
    );

    const mobileDialogCall = useMemo(() => {
        if (!mobileDialogCallId) {
            return null;
        }

        return pageCalls.find((call) => call.id === mobileDialogCallId) ?? null;
    }, [mobileDialogCallId, pageCalls]);

    useVoiceCallRealtimeRefresh({
        organizationId,
        selectedCallId,
        // Newly created calls only affect the newest page; revalidating every
        // historical page would multiply the office-filter query again.
        refreshCallsPage: () => refreshFirstCallsPage(),
        refreshSelectedCallDetail: () => mutateSelectedCallDetail(),
    });

    const handleSelectCall = (callId: string) => {
        setDismissedCardCallId(null);
        selectVoiceCall(callId);
    };
    const callListSentinelRef = useRef<HTMLDivElement | null>(null);
    // Tall viewports can fit the first page without a scrollbar. Observe the
    // bottom of the list so pagination also runs without a scroll event.
    useEffect(() => {
        const sentinel = callListSentinelRef.current;
        if (
            !sentinel ||
            !hasMoreCalls ||
            isLoadingMoreCalls ||
            canSearchOlderCalls ||
            callsError
        ) {
            return;
        }
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry?.isIntersecting) {
                    observer.disconnect();
                    loadMoreCalls();
                }
            },
            {
                root: sentinel.parentElement,
                rootMargin: `0px 0px ${INFINITE_SCROLL_THRESHOLD_PX}px 0px`,
            },
        );
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [
        hasMoreCalls,
        isLoadingMoreCalls,
        canSearchOlderCalls,
        callsError,
        loadMoreCalls,
        pageCalls.length,
    ]);

    useEffect(() => {
        if (!hasFilterChanges) {
            return;
        }

        setAppliedFilters({
            ...pendingFiltersForApi,
        });
        selectVoiceCall(null, { replace: true });
    }, [hasFilterChanges, pendingFiltersForApi, selectVoiceCall]);

    const handleClearFilters = () => {
        setPendingCaregiverId(ALL_CAREGIVERS_VALUE);
        setPendingClientId(ALL_CLIENTS_VALUE);
        setPendingDateFrom(undefined);
        setPendingDateTo(undefined);
        setPendingDirection(ALL_DIRECTIONS_VALUE);
        setPendingCallType(ALL_CALL_TYPES_VALUE);
        setPendingTransferStatus(ALL_TRANSFER_STATUSES_VALUE);
        setPendingTransferDestinationId(ALL_TRANSFER_DESTINATIONS_VALUE);
        setPendingLineNumber(ALL_LINES_VALUE);
        setPendingVoicemailStatus(ALL_VOICEMAIL_STATUSES_VALUE);
        setPendingMessageLeft(false);
        setPendingNotesFilter(ALL_NOTES_VALUE);
        setPendingTopic(ALL_TOPICS_VALUE);
        setPendingSearch('');
        setPendingOfficeIds([]);
        setAppliedFilters({
            ...EMPTY_FILTERS,
            direction: undefined,
        });
        selectVoiceCall(null, { replace: true });
    };

    if (isWaitingForDeepLinkOrganization || !organizationId) {
        return null;
    }

    const mobileDialogCallerName = mobileDialogCall?.caller_name ?? null;
    const selectedCallCaregiverName =
        selectedCallListItem?.caller_name ??
        selectedCallDetail?.caller_name ??
        null;
    const headerActions = <TrainingModePageTourReplayButton />;

    // A person with no visible thread gets the call card on its own. Staff
    // callers land here too: their notification thread is not a caller
    // thread, so the Calls tab shows the call instead of those messages.
    const threadPaneContent =
        threadCall === null || !organizationId ? null : threadConversationId ? (
            <OrganizationConversationDetailPane
                conversationId={threadConversationId}
                fillThreadWidth={true}
                showVoiceCallHeaderActions={true}
                markAsRead={markAsRead}
                markAsUnread={markAsUnread}
                openVoiceCallId={
                    selectedCallId === dismissedCardCallId
                        ? null
                        : selectedCallId
                }
                onOpenVoiceCallDismissed={() =>
                    setDismissedCardCallId(selectedCallId)
                }
                organizationId={organizationId}
            />
        ) : isLoadingThreadConversation || isPageLoading ? (
            <div
                className="flex items-center justify-center py-12"
                role="status"
                aria-label="Loading call conversation"
            >
                <Spinner size="lg" tone="muted" />
            </div>
        ) : null;
    const selectedCallDetailContent =
        (isLoadingSelectedCallDetail || isResolvingAcrossOrganizations) &&
        !selectedCall ? (
            <div className="flex items-center justify-center py-12">
                <Spinner size="lg" tone="muted" />
            </div>
        ) : !selectedCall ? (
            <p className="text-sm text-red-600 dark:text-red-400">
                Unable to load call details.
            </p>
        ) : (
            <div className="relative h-full min-h-0">
                <CallDetailDialog
                    caregiverName={selectedCallCaregiverName}
                    open={true}
                    onOpenChange={(open) => {
                        if (!open) {
                            selectVoiceCall(null, { replace: true });
                        }
                    }}
                    organizationId={organizationId}
                    prefetchedCall={selectedCall}
                    presentation="pane"
                    timezone={timezone}
                    voiceCallId={selectedCall.id}
                />
            </div>
        );

    // v3 Filter menu and active chips share the messages inbox FilterBar.
    const voiceCallFilterDimensions = buildVoiceCallFilterDimensions({
        offices: officeOptions.map((office) => ({
            value: office.id,
            label: office.display_name,
        })),
        lines: lineOptions.map((line) => ({
            value: line.number,
            label:
                line.label?.trim() ||
                formatPhoneNumber(line.number) ||
                line.number,
        })),
        transferDestinations: transferDestinationOptions.map((destination) => ({
            value: destination.id,
            label: destination.name ?? 'Unknown destination',
        })),
        topics: CALL_TOPIC_OPTIONS,
        caregivers: (filterParticipants?.caregivers ?? []).map((caregiver) => ({
            value: caregiver.id,
            label: formatName(caregiver.name),
        })),
        clients: (filterParticipants?.clients ?? []).map((client) => ({
            value: client.id,
            label: formatName(formatClientName(client)),
        })),
        dateFrom: pendingDateFrom,
        dateTo: pendingDateTo,
        onDateFromChange: setPendingDateFrom,
        onDateToChange: setPendingDateTo,
    });
    const voiceCallChips = voiceCallFilterChips({
        office: pendingOfficeIds,
        line: pendingLineNumber === ALL_LINES_VALUE ? [] : [pendingLineNumber],
        call_type:
            pendingCallType === ALL_CALL_TYPES_VALUE ? [] : [pendingCallType],
        transfer:
            pendingTransferStatus === ALL_TRANSFER_STATUSES_VALUE
                ? []
                : [pendingTransferStatus],
        transfer_destination:
            pendingTransferDestinationId === ALL_TRANSFER_DESTINATIONS_VALUE
                ? []
                : [pendingTransferDestinationId],
        voicemail:
            pendingVoicemailStatus === ALL_VOICEMAIL_STATUSES_VALUE
                ? []
                : [pendingVoicemailStatus],
        notes:
            pendingNotesFilter === ALL_NOTES_VALUE ? [] : [pendingNotesFilter],
        topic: pendingTopic === ALL_TOPICS_VALUE ? [] : [pendingTopic],
        caregiver:
            pendingCaregiverId === ALL_CAREGIVERS_VALUE
                ? []
                : [pendingCaregiverId],
        client: pendingClientId === ALL_CLIENTS_VALUE ? [] : [pendingClientId],
        date_from: pendingDateFrom ? [pendingDateFrom] : [],
        date_to: pendingDateTo ? [pendingDateTo] : [],
    });
    const handleVoiceCallChipsChange = (
        chips: Array<{
            readonly dimension: VoiceCallFilterKey;
            readonly values: Array<string>;
        }>,
    ) => {
        const values = voiceCallFilterChipValues(chips);
        const first = (key: VoiceCallFilterKey) => values[key]?.[0];
        setPendingOfficeIds([...(values.office ?? [])]);
        setPendingLineNumber(first('line') ?? ALL_LINES_VALUE);
        const callType = first('call_type');
        setPendingCallType(
            callType === 'client' ||
                callType === 'caregiver' ||
                callType === 'notification'
                ? callType
                : ALL_CALL_TYPES_VALUE,
        );
        const transfer = first('transfer');
        setPendingTransferStatus(
            transfer === 'transferred' || transfer === 'not_transferred'
                ? transfer
                : ALL_TRANSFER_STATUSES_VALUE,
        );
        setPendingTransferDestinationId(
            first('transfer_destination') ?? ALL_TRANSFER_DESTINATIONS_VALUE,
        );
        const voicemail = first('voicemail');
        setPendingVoicemailStatus(
            voicemail === 'voicemail' || voicemail === 'not_voicemail'
                ? voicemail
                : ALL_VOICEMAIL_STATUSES_VALUE,
        );
        setPendingNotesFilter(
            first('notes') === NEEDS_REVIEW_NOTES_VALUE
                ? NEEDS_REVIEW_NOTES_VALUE
                : ALL_NOTES_VALUE,
        );
        const topic = first('topic');
        setPendingTopic(
            topic !== undefined && isCallTopicValue(topic)
                ? topic
                : ALL_TOPICS_VALUE,
        );
        // Caregiver and client filters are mutually exclusive, and neither
        // applies to notification calls, matching the legacy popover rules.
        const caregiverId = first('caregiver');
        const clientId = first('client');
        setPendingCaregiverId(
            callType === 'notification' || callType === 'client' || !caregiverId
                ? ALL_CAREGIVERS_VALUE
                : caregiverId,
        );
        setPendingClientId(
            callType === 'notification' ||
                callType === 'caregiver' ||
                !clientId ||
                (caregiverId !== undefined &&
                    pendingCaregiverId !== caregiverId)
                ? ALL_CLIENTS_VALUE
                : clientId,
        );
        setPendingDateFrom(first('date_from'));
        setPendingDateTo(first('date_to'));
    };
    const filtersBlock = (
        <Fragment>
            <SearchInput
                containerClassName="w-full min-w-0"
                onValueChange={setPendingSearch}
                placeholder="Search…"
                value={pendingSearch}
            />
            <div
                // Same one-line pill row as the messages inbox, including its
                // negative margins so the pills sit flush with the search box.
                className="group/fit -mx-2 -my-1 flex items-center gap-1 px-2 py-1"
                data-testid="voice-calls-filters"
                role="toolbar"
                aria-label="Call filters"
            >
                <InboxFilterPill
                    active={pendingDirection === 'inbound'}
                    label="Inbound"
                    onClick={() =>
                        setPendingDirection((previous) =>
                            previous === 'inbound'
                                ? ALL_DIRECTIONS_VALUE
                                : 'inbound',
                        )
                    }
                />
                <InboxFilterPill
                    active={pendingDirection === 'outbound'}
                    label="Outbound"
                    onClick={() =>
                        setPendingDirection((previous) =>
                            previous === 'outbound'
                                ? ALL_DIRECTIONS_VALUE
                                : 'outbound',
                        )
                    }
                />
                <InboxFilterPill
                    active={pendingMessageLeft}
                    icon={Mail}
                    label="Messages"
                    onClick={() =>
                        setPendingMessageLeft((previous) => !previous)
                    }
                />
                <FilterBar
                    chips={voiceCallChips}
                    dimensions={voiceCallFilterDimensions}
                    layout="trigger"
                    onChange={handleVoiceCallChipsChange}
                    triggerClassName={INBOX_PILL_COMPACT_CLASS}
                    triggerVariant="labeled"
                />
            </div>
            <FilterBar
                chips={voiceCallChips}
                dimensions={voiceCallFilterDimensions}
                layout="active-row"
                onChange={handleVoiceCallChipsChange}
                onClear={handleClearFilters}
                showClear={voiceCallChips.length > 0}
            />
        </Fragment>
    );

    const listBlock = (
        <div
            className="min-h-0 flex-1 overflow-y-auto"
            data-testid="voice-call-list"
        >
            {isPageLoading && pageCalls.length === 0 ? (
                <div className="flex items-center justify-center py-10">
                    <Spinner size="lg" tone="muted" />
                </div>
            ) : callsError && pageCalls.length === 0 ? (
                <p className="p-4 text-sm text-red-600 dark:text-red-400">
                    Unable to load voice calls.
                </p>
            ) : pageCalls.length === 0 ? (
                <VoiceCallsEmptyState
                    hasActiveFilters={hasActiveFilters}
                    canSearchOlderCalls={canSearchOlderCalls}
                    isLoadingMore={isLoadingMoreCalls}
                    onSearchOlderCalls={loadMoreCalls}
                />
            ) : (
                <Fragment>
                    <CallListRows
                        calls={pageCalls}
                        onSelect={handleSelectCall}
                        selectedCallId={isMobile ? null : selectedCallId}
                        silentListeningLabelSettingsEnabled={
                            silentListeningLabelSettingsEnabled
                        }
                        timezone={timezone}
                    />
                    {hasMoreCalls && !canSearchOlderCalls && (
                        <div
                            ref={callListSentinelRef}
                            className="h-px"
                            data-testid="voice-call-list-sentinel"
                        />
                    )}
                    {isLoadingMoreCalls && (
                        <div className="flex items-center justify-center py-4">
                            <Spinner tone="muted" />
                        </div>
                    )}
                    {canSearchOlderCalls && (
                        <div className="flex justify-center py-4">
                            <SearchOlderVoiceCallsButton
                                isLoading={isLoadingMoreCalls}
                                onSearch={loadMoreCalls}
                            />
                        </div>
                    )}
                    {!hasMoreCalls && !isLoadingMoreCalls && (
                        <p className="text-muted-foreground py-4 text-center text-xs">
                            End of voice calls
                        </p>
                    )}
                </Fragment>
            )}
        </div>
    );

    // Mirrors the conversations inbox: the list column on the left with the
    // page tabs, search, and filters in its header; the selected call opens
    // in the pane where a conversation thread would.
    return (
        <TrainingModePageTourScope
            pageId="voice-calls"
            organizationId={organizationId}
            ready={!isPageLoading}
            steps={VOICE_CALLS_PAGE_TOUR_STEPS}
        >
            <div className="bg-background relative flex h-full min-h-0 w-full overflow-hidden">
                <div className="@container/inbox relative flex h-full min-h-0 w-full shrink-0 flex-col overflow-hidden border-r md:w-[max(24rem,27vw)] md:max-w-[36rem]">
                    <div className="shrink-0 space-y-3 px-3 pt-4 pb-3">
                        <div className="px-1">
                            <ConversationsPageTabs
                                active="calls"
                                actions={headerActions}
                                phoneActions={<PhoneAppBarTourReplayButton />}
                            />
                        </div>
                        {filtersBlock}
                    </div>
                    {listBlock}
                </div>
                {!isMobile && (
                    <div
                        className="hidden min-h-0 min-w-0 flex-1 flex-col overflow-hidden md:flex"
                        data-testid="voice-calls-detail-pane"
                    >
                        {selectedCallId ? (
                            // The detail pane owns its header, scrolling, and
                            // side panel so the caller name lands where a
                            // conversation title does; only the loading and
                            // error states need an inset here.
                            <div
                                className={cn(
                                    'flex min-h-0 flex-1 flex-col',
                                    !selectedCall && 'p-4',
                                )}
                            >
                                {threadPaneContent ?? selectedCallDetailContent}
                            </div>
                        ) : (
                            <EmptyState
                                icon={Phone}
                                title="No call selected"
                                description="Pick a call on the left."
                                className="mt-12"
                            />
                        )}
                    </div>
                )}
                {isMobile && (
                    <CallDetailDialog
                        organizationId={organizationId}
                        voiceCallId={mobileDialogCallId}
                        prefetchedCall={mobileDialogCall}
                        caregiverName={mobileDialogCallerName}
                        open={mobileDialogCallId !== null}
                        onOpenChange={(open) => {
                            if (!open) {
                                selectVoiceCall(null, { replace: true });
                            }
                        }}
                        timezone={timezone}
                    />
                )}
            </div>
        </TrainingModePageTourScope>
    );
}
